'use client';

/**
 * `useTrace`, the pass laid back over the piece.
 *
 * ## What it answers
 *
 * A traversal is **531 attacks in the median, over 6 min 12**. When it ends
 * the panel says one sentence about it — where the player stopped, how many
 * attacks went unanswered — and the rail says roughly where. Neither can
 * answer the question the player actually has, which is *what did I do
 * there*. The reading exists, note by note: the live piano lane holds every
 * strike of the pass, in ticks, against the score's own grid. It was simply
 * never shown.
 *
 * ## Why it is a mode and not a layer
 *
 * Because it cannot be read while anything moves. The trace is a contour on
 * top of the notation — six pixels tall in places — and the roll shows about
 * **3% of a traversal** at a time. Read while scrolling it is a flicker; read
 * at rest, on a still roll the player scrolls by hand, it is a document.
 *
 * So opening it parks: `animateScrollToTick` pauses the transport on its way
 * and leaves the view on the place the pass went wrong. That park is the
 * whole feature. A trace that opens on bar 1 of a 78-bar piece asks the
 * player to go and find their own mistake, which is the thing they just
 * failed to do.
 *
 * ## The accident that makes it safe
 *
 * A paused transport disables the live lane (`PianoControler`), and a
 * disabled lane does not record. So the trace **cannot be written over while
 * it is being read**: the player can lean on the keyboard, try the passage,
 * work out what their hand was doing, and the record of the pass is
 * untouched. That is not arranged here, but it is relied upon.
 */

import { useCallback, useEffect, useState } from 'react';
import { useStudio } from '@/contexts/studio-context';

export type UseTraceResult = {
  /** Whether the roll is currently drawing the trace. */
  readonly open: boolean;
  /** Open it, and park the view on `parkTick`. */
  readonly show: () => void;
  /** Close it. Idempotent — safe to call from every exit. */
  readonly hide: () => void;
};

/**
 * @param parkTick Where the reading should begin. `null` parks nowhere and
 *   leaves the view where the pass left it.
 */
export function useTrace(parkTick: number | null): UseTraceResult {
  const { studio } = useStudio();
  const [open, setOpen] = useState(false);

  // Applied in an effect and undone by its cleanup, so a player who leaves
  // the page with the trace open never leaves the roll drawing contours over
  // the next piece. `'hidden'` and not `'notes'`: the journey owns the lane
  // for as long as it is mounted, and it is the page that gives it back.
  useEffect(() => {
    if (studio === null || !open) {
      return;
    }

    studio.setLiveLaneMode('trace');

    return () => {
      studio.setLiveLaneMode('hidden');
    };
  }, [studio, open]);

  const hide = useCallback(() => {
    setOpen(false);
  }, []);

  const show = useCallback(() => {
    setOpen(true);

    if (studio === null || parkTick === null) {
      return;
    }

    void studio.animateScrollToTick(parkTick);
  }, [studio, parkTick]);

  // A mode needs a way out that is not a button. Escape is free here — the
  // run shortcuts only claim it while a pass is running, and a pass running
  // is the one state the trace is never open in.
  useEffect(() => {
    if (!open) {
      return;
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }

      e.preventDefault();
      setOpen(false);
    };

    document.addEventListener('keydown', handler);

    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [open]);

  return { open, show, hide };
}
